import axios from 'axios';
import axiosInstance from './axiosCreator';
import type {
  Flashcard,
  FlashcardContent,
  FlashcardScheduling,
} from '../types/Flashcard';
import { normalizeFlashcard } from '../utils/flashcardVariants';

interface FlashcardUpdateData {
  front: string;
  back: string;
  explanation: string;
  lastEditedById?: string;
}

// Mark a flashcard as priority or not
export const updateLinkedFlashcardAssignments = async (
  flashcards: Array<{
    flashcardId: string;
    isLinked: boolean;
    vignetteId?: string;
    liveQuestionId?: string;
  }>
) => {
  try {
    const response = await axiosInstance.post('/api/v5/flashcards/priority', {
      flashcards,
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Update Flashcard by ID
export const updateFlashcardBE = async (
  id: string,
  data: {
    flashcardYield: number | null;
    firstSessionOrd: number | null;
    exams: string[];
    lastEditedById?: string;
  }
) => {
  try {
    const response = await axiosInstance.put(`/flashcards/parent/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Update Flashcard Variant by ID
export const updateFlashcardVariantBE = async (
  variantId: string,
  data: FlashcardUpdateData
) => {
  try {
    const response = await axiosInstance.put(
      `/flashcards/variant/${variantId}`,
      data
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Get all user suspended flashcards
export const getSuspendedFlashcards = async () => {
  try {
    const response = await axiosInstance.get(`/flashcards/suspended`);
    return Array.isArray(response.data)
      ? response.data.map(normalizeFlashcard)
      : [];
  } catch (error) {
    console.error(error);
  }
};

// User suspend a flashcard
export const suspendFlashcard = async (flashcardId: string) => {
  try {
    const response = await axiosInstance.post(
      `/flashcards/${flashcardId}/suspend`
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// User unsuspend a flashcard
export const unsuspendFlashcard = async (flashcardId: string) => {
  try {
    const response = await axiosInstance.post(
      `/flashcards/${flashcardId}/unsuspend`
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Set Flashcard isQAed
export const setFlashcardQAStatus = async (id: string, isQAed: boolean) => {
  try {
    const response = await axiosInstance.put(`/flashcards/${id}/qa`, {
      isQAed,
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getFlashcardHistory = async (flashcardId: string) => {
  try {
    const response = await axiosInstance.get(`/flashcards/${flashcardId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

type FlashcardStarToggleResponse = {
  success: boolean;
  data?: {
    flashcardId: string;
    isStarred: boolean;
  };
};

export const starFlashcardV5 = async (
  flashcardId: string
): Promise<FlashcardStarToggleResponse> => {
  try {
    const response = await axiosInstance.put(
      `/api/v5/flashcards/${flashcardId}/star`
    );

    return response.data as FlashcardStarToggleResponse;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const unstarFlashcardV5 = async (
  flashcardId: string
): Promise<FlashcardStarToggleResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/api/v5/flashcards/${flashcardId}/star`
    );

    return response.data as FlashcardStarToggleResponse;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export type FlashcardFilterAmount = 'all' | 'count';
export type FlashcardFilterType = 'new' | 'due' | 'both';

export interface FlashcardFilterRequest {
  examId?: string;
  topicIds?: string[];
  lectureIds?: string[];
  amount: FlashcardFilterAmount;
  count?: number;
  type: FlashcardFilterType;
}

export interface FlashcardCountRequest {
  examId?: string;
  topicIds?: string[];
  lectureIds?: string[];
}

export interface WorkloadFlashcardCountRequest {
  examId: string;
}

export type FlashcardCountSnapshot = {
  total?: number;
  new?: number;
  due?: number;
  scheduled?: number;
  suspended?: number;
  // New cards linked to a vignette answered today (today-session context only).
  linkedToday?: number;
  // All outstanding NEW cards linked to a QBank vignette for the exam (not
  // time-boxed to today). Drives the "Linked" study option.
  linked?: number;
};

type LinkedFlashcardVariantV2Dto = {
  id: string;
  ord: number;
  front: string;
  back: string;
  explanation: string | null;
  explanationShort: string | null;
  references: string[];
  imageLinks?: FlashcardContent['imageLinks'];
};

type LinkedFlashcardSchedulingV2Dto = {
  due: string;
  stability: number;
  difficulty: number;
  elapsedDays: number;
  scheduledDays: number;
  reps: number;
  lapses: number;
  state: number;
  lastReview: string;
  difficultyIndex: number;
  linkedAt: string | null;
  linkedVignetteId: string | null;
  isSuspended: boolean;
};

type LinkedFlashcardV2Dto = {
  id: string;
  schemaVersion: 'v2';
  variants: LinkedFlashcardVariantV2Dto[];
  examLinks: Array<{ examId: string; yield: number | null }>;
  scheduling: LinkedFlashcardSchedulingV2Dto;
  isSuspended: boolean;
  isQAed: boolean;
};

type LinkedFlashcardLinkV2Dto = {
  flashcardId: string;
  answerOptionId: string | null;
  flashcard: LinkedFlashcardV2Dto;
  runtime: {
    isLinked: boolean;
    isNew: boolean;
  };
};

type LinkedFlashcardsV2ResponseDto = {
  vignetteId?: string;
  liveQuestionId?: string;
  links: LinkedFlashcardLinkV2Dto[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isLinkedFlashcardsV2Response = (
  payload: unknown
): payload is LinkedFlashcardsV2ResponseDto =>
  isRecord(payload) &&
  (typeof payload.vignetteId === 'string' ||
    typeof payload.liveQuestionId === 'string') &&
  Array.isArray(payload.links);

const mapLinkedFlashcardLinkV2ToFlashcard = (
  link: LinkedFlashcardLinkV2Dto
): Flashcard => {
  const scheduling: FlashcardScheduling = {
    ...link.flashcard.scheduling,
    linkedAt: link.flashcard.scheduling.linkedAt ?? undefined,
    linkedVignetteId: link.flashcard.scheduling.linkedVignetteId ?? undefined,
  };

  return normalizeFlashcard({
    id: link.flashcard.id,
    schemaVersion: link.flashcard.schemaVersion,
    variants: link.flashcard.variants.map((variant) => ({
      id: variant.id,
      ord: variant.ord,
      front: variant.front,
      back: variant.back,
      explanation: variant.explanation ?? '',
      explanationShort: variant.explanationShort ?? null,
      references: variant.references,
      imageLinks: variant.imageLinks,
    })),
    examLinks: link.flashcard.examLinks,
    scheduling,
    isSuspended: link.flashcard.isSuspended,
    isQAed: link.flashcard.isQAed,
    linkedAt: scheduling.linkedAt,
    linkedVignetteId: scheduling.linkedVignetteId,
    isLinked: link.runtime.isLinked,
    isNew: link.runtime.isNew,
    vignetteLinkAnswerOptionId: link.answerOptionId,
  });
};

export const fetchFlashcardCounts = async (payload: FlashcardCountRequest) => {
  try {
    const response = await axiosInstance.post(
      '/api/v5/flashcards/count',
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching flashcard counts:', error);
    throw error;
  }
};

export const fetchWorkloadFlashcardCounts = async (
  payload: WorkloadFlashcardCountRequest
) => {
  try {
    const response = await axiosInstance.post(
      '/api/v5/flashcards/count/workload',
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching workload flashcard counts:', error);
    throw error;
  }
};

export const fetchLinkedFlashcardsV5 = async (vignetteId: string) => {
  try {
    const response = await axiosInstance.get(
      `/api/v5/qbank/vignettes/${vignetteId}/flashcards`
    );

    if (isLinkedFlashcardsV2Response(response.data)) {
      return response.data.links.map(mapLinkedFlashcardLinkV2ToFlashcard);
    }
    return Array.isArray(response.data)
      ? response.data.map(normalizeFlashcard)
      : [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      return [];
    }

    console.error('Error fetching v5 linked flashcards:', error);
    throw error;
  }
};

export const fetchLinkedFlashcardsForLiveQuestionV5 = async (
  liveQuestionId: string
) => {
  try {
    const response = await axiosInstance.get(
      `/api/v5/live-questions/${liveQuestionId}/flashcards`
    );

    if (isLinkedFlashcardsV2Response(response.data)) {
      return response.data.links.map(mapLinkedFlashcardLinkV2ToFlashcard);
    }
    return Array.isArray(response.data)
      ? response.data.map(normalizeFlashcard)
      : [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      return [];
    }

    console.error('Error fetching v5 live-question linked flashcards:', error);
    throw error;
  }
};
